import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Info,
  X,
} from "lucide-react";
import { Select } from "antd";
import { AppToast, type ToastType } from "../../components/common/AlertMessage";
import { MiniPaginator } from "../../components/common/MiniPaginator";
import {
  createInvoiceApplicationApi,
  downloadInvoiceApplicationApi,
  getInvoiceableOrdersApi,
  getInvoiceSummaryApi,
  getInvoiceApplicationDetailApi,
  getMyInvoiceApplicationsApi,
  INVOICE_STATUS_CODE_MAP,
  INVOICE_STATUS_LABEL_MAP,
  INVOICE_TYPE_CODE_MAP,
  INVOICE_TYPE_LABEL_MAP,
  resubmitInvoiceApplicationApi,
  TITLE_TYPE_CODE_MAP,
  TITLE_TYPE_LABEL_MAP,
  USER_STATUS_LABEL_MAP,
  type InvoiceableOrderPagedResp,
  type InvoiceApplicationRespVO,
  type InvoiceableOrderRespVO,
  type InvoiceStatusCode,
  type UserInvoiceStatusCode,
} from "../../service/invoice";
import "./InvoiceManagement.scss";

type InvoiceStatusKey = "可开票" | "开票中" | "待修改" | "已开票" | "已作废";
type InvoiceRecordStatusKey = "开票中" | "待修改" | "已开票" | "已作废";
type InvoiceTypeKey = "电子普通发票" | "电子专用发票";
type TitleTypeKey = "企业" | "个人";

/** 统一社会信用代码：15 或 18 位，字符集排除 I/O/S/Z（与后端 schema 一致） */
const TAX_NO_REGEX = /^[0-9A-HJ-NPQRTUWXY]{15}$|^[0-9A-HJ-NPQRTUWXY]{18}$/;
/** 邮箱：仅做最基础的格式校验，与后端 email format 对齐 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface OrderRow {
  /** 后端订单 ID（数字），提交开票申请时必填 */
  orderDbId: number;
  orderNo: string;
  invoiceStatus: InvoiceStatusKey;
  /** 内部展示用的开票状态 code，便于回传筛选 */
  invoiceStatusCode: InvoiceStatusCode | "";
  amount: number;
  /** 最近一次申请 ID（用于「修改申请」入口） */
  applicationId?: number;
  /** 退回原因（编码） */
  rejectionReason?: string;
  /** 退回原因中文文案（后端直接给） */
  rejectionReasonLabel?: string;
  /** 退回补充说明（拼接在 label 后面的备注） */
  rejectionNote?: string;
  /** 订单内容（积分文案） */
  content: string;
  /** 支付时间 */
  payTime: string;
  /** 是否允许提交/修改开票申请（后端控制） */
  canApply?: boolean;
  /** 订单类型（来自后端，回传给开票申请接口，例如 MEMBER_ORDER） */
  orderType?: string;
}

interface InvoiceRow {
  /** 后端申请 ID，提交/下载/重新提交都靠它 */
  applicationDbId: number;
  invoiceNo: string;
  /** 关联的订单号（详情接口会回填，列表里可能为空） */
  orderNo: string;
  title: string;
  type: string;
  amount: number;
  appliedAt: string;
  status: InvoiceRecordStatusKey;
  /** 用户侧状态编码 */
  userStatusCode: UserInvoiceStatusCode | "";
  /** 后端是否允许下载发票 */
  downloadable?: boolean;
  /** 邮件回执邮箱（详情接口才有） */
  email?: string;
}

/** 退回原因编码 → 中文（按 doc/发票接口.json 的 reason 编码） */
const REJECTION_REASON_LABEL: Record<string, string> = {
  TITLE_MISMATCH: "发票抬头与订单主体不一致",
  TAX_NO_INVALID: "企业税号不完整或有误",
  EMAIL_INVALID: "接收邮箱有误",
  OTHER: "其他",
};

/** 中文标签 → userStatus 编码（把 toolbar 的过滤项反向映射回接口参数） */
const USER_STATUS_LABEL_MAP_BY_LABEL: Record<string, UserInvoiceStatusCode> =
  {
    开票中: "APPLYING",
    待修改: "NEED_FIX",
    已开票: "ISSUED",
    已作废: "VOIDED",
  };

const TAG_CLASS_MAP: Record<InvoiceStatusKey, string> = {
  可开票: "blue",
  开票中: "amber",
  待修改: "red",
  已开票: "green",
  已作废: "red",
};

const INVOICE_TAG_CLASS_MAP: Record<InvoiceRecordStatusKey, string> = {
  开票中: "amber",
  待修改: "red",
  已开票: "green",
  已作废: "red",
};

const INVOICE_STATUS_OPTIONS = [
  { value: "", label: "全部开票状态" },
  { value: "可开票", label: "可开票" },
  { value: "开票中", label: "开票中" },
  { value: "待修改", label: "待修改" },
  { value: "已开票", label: "已开票" },
  { value: "已作废", label: "已作废" },
];

const INVOICE_RECORD_STATUS_OPTIONS = [
  { value: "", label: "全部状态" },
  { value: "开票中", label: "开票中" },
  { value: "待修改", label: "待修改" },
  { value: "已开票", label: "已开票" },
  { value: "已作废", label: "已作废" },
];

const formatCurrency = (n: number): string => `¥${Number(n || 0).toFixed(2)}`;

/** 开票记录每页条数（按用户要求固定为 5） */
const INVOICE_PAGE_SIZE = 5;
/** 充值订单每页条数（前端切片，因为 /video/order/invoiceable 不支持分页） */
const ORDER_PAGE_SIZE = 5;

/** 把后端订单行映射为组件内部 OrderRow（保留所有展示字段） */
const mapOrder = (o: InvoiceableOrderRespVO): OrderRow => ({
  orderDbId: o.orderId,
  orderNo: o.orderNo,
  invoiceStatus:
    INVOICE_STATUS_LABEL_MAP[o.invoiceStatus] ||
    (o.invoiceStatusLabel as InvoiceStatusKey) ||
    "可开票",
  invoiceStatusCode: o.invoiceStatus,
  amount: Number(o.amountYuan || 0),
  applicationId: o.applicationId,
  rejectionReason: o.rejectionReason,
  rejectionReasonLabel: o.rejectionReasonLabel,
  rejectionNote: o.rejectionNote,
  content: o.orderContent || "",
  payTime: o.payTime || "",
  canApply: o.canApply,
  orderType: o.orderType,
});

/** 把后端开票申请映射为组件内部 InvoiceRow */
const mapApplication = (a: InvoiceApplicationRespVO): InvoiceRow => {
  const statusKey =
    USER_STATUS_LABEL_MAP[a.userStatus] ||
    (a.userStatusLabel as InvoiceRecordStatusKey) ||
    "开票中";
  return {
    orderNo: a.orderNo || "",
    applicationDbId: a.id,
    invoiceNo: a.invoiceNo,
    title: a.titleName,
    type: INVOICE_TYPE_LABEL_MAP[a.invoiceType] || a.invoiceTypeLabel || "",
    amount: Number(a.amountYuan || 0),
    appliedAt:
      a.submittedTime || a.createTime || a.updateTime || "",
    status: statusKey,
    userStatusCode: a.userStatus,
    downloadable: a.downloadable,
    email: a.email,
  };
};

/** 友好化 API 返回的提示消息（避免把英文 code 直接抛给用户） */
const extractErrorMessage = (err: any, fallback: string): string => {
  if (!err) return fallback;
  if (typeof err === "string") return err;
  return (
    err?.response?.data?.msg ||
    err?.msg ||
    err?.message ||
    fallback
  );
};

/** 去除 undefined / null，避免被 qs 序列化成 `key=undefined` 把后端过滤搞坏 */
const cleanParams = <T extends Record<string, any>>(params: T): Partial<T> => {
  const out: Partial<T> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    out[k as keyof T] = v;
  });
  return out;
};

/**
 * 账户与账单 · 充值订单与发票管理
 * 基于 doc/31.账户与账单.html 实现（与原型一致）。
 */
export const InvoiceManagement: FC = () => {
  const [activeTab, setActiveTab] = useState<"orders" | "invoices">("orders");
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [summary, setSummary] = useState<{
    totalRecharge: number;
    invoiceableAmount: number;
  } | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  /** 修改申请回填的源申请 ID（用于「重新提交」路由） */
  const [editingApplicationId, setEditingApplicationId] = useState<
    number | null
  >(null);
  /** 发票记录分页 */
  const [invoicePageNo, setInvoicePageNo] = useState(1);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  /** 订单分页（换页时重新请求后端） */
  const [orderPageNo, setOrderPageNo] = useState(1);
  const [orderTotal, setOrderTotal] = useState(0);

  // 订单列表过滤
  const [orderKeyword, setOrderKeyword] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState("");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("");

  // Modal 状态
  const [modalOpen, setModalOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState<OrderRow | null>(null);
  const [invoiceType, setInvoiceType] = useState<InvoiceTypeKey>(
    "电子普通发票",
  );
  const [titleType, setTitleType] = useState<TitleTypeKey>("企业");
  const [invoiceTitle, setInvoiceTitle] = useState("");
  const [invoiceTax, setInvoiceTax] = useState("");
  const [invoiceEmail, setInvoiceEmail] = useState("");

  // Toast（项目统一的顶部 AppToast）
  const [toastConfig, setToastConfig] = useState<{
    type: ToastType;
    message: string;
  } | null>(null);

  /** 累计充值（优先用后端汇总） */
  const totalRecharge = useMemo(
    () =>
      summary ? summary.totalRecharge : orders.reduce((s, o) => s + o.amount, 0),
    [orders, summary],
  );
  /** 当前可开票金额（优先用后端汇总） */
  const invoiceableAmount = useMemo(
    () =>
      summary
        ? summary.invoiceableAmount
        : orders
            .filter((o) => o.invoiceStatus === "可开票")
            .reduce((s, o) => s + o.amount, 0),
    [orders, summary],
  );

  const isSpecialInvoice = invoiceType === "电子专用发票";

  const showToast = useCallback((message: string, type: ToastType = "info") => {
    setToastConfig({ type, message });
  }, []);

  // 拉取汇总
  const refreshSummary = useCallback(async () => {
    try {
      const resp = await getInvoiceSummaryApi();
      if (resp?.data) setSummary(resp.data);
    } catch (err) {
      // 汇总失败仅静默（不影响列表）
      console.warn("[invoice] summary failed", err);
    }
  }, []);

  // 拉取可开票订单（服务端分页：换页 = 重新请求）
  const refreshOrders = useCallback(
    async (
      keyword?: string,
      statusFilter?: string,
      pageNo: number = orderPageNo,
    ) => {
      setLoadingOrders(true);
      try {
        const statusCode =
          statusFilter && INVOICE_STATUS_CODE_MAP[statusFilter]
            ? INVOICE_STATUS_CODE_MAP[statusFilter]
            : undefined;
        const resp = await getInvoiceableOrdersApi(
          cleanParams({
            pageNo,
            pageSize: ORDER_PAGE_SIZE,
            keyword: keyword || undefined,
            invoiceStatus: statusCode,
          }),
        );
        // 兼容旧返回（data 直接是数组）与新返回（data 是 { list, total }）
        const paged: InvoiceableOrderPagedResp = Array.isArray(resp?.data)
          ? { list: (resp?.data as InvoiceableOrderRespVO[]) || [], total: ((resp?.data as InvoiceableOrderRespVO[]) || []).length }
          : {
              list: (resp?.data as { list?: InvoiceableOrderRespVO[] })?.list || [],
              total: (resp?.data as { total?: number })?.total ?? 0,
            };
        setOrders(paged.list.map(mapOrder));
        setOrderTotal(paged.total);
      } catch (err) {
        showToast(extractErrorMessage(err, "加载订单失败"), "error");
      } finally {
        setLoadingOrders(false);
      }
    },
    [orderPageNo, showToast],
  );

  // 拉取我的开票申请（分页）
  const refreshInvoices = useCallback(
    async (statusFilter?: string, pageNo: number = invoicePageNo) => {
      setLoadingInvoices(true);
      try {
        const resp = await getMyInvoiceApplicationsApi(
          cleanParams({
            pageNo,
            pageSize: INVOICE_PAGE_SIZE,
            userStatus:
              (statusFilter &&
                USER_STATUS_LABEL_MAP_BY_LABEL[statusFilter]) ||
              undefined,
          }),
        );
        const list = resp?.data?.list || [];
        const total = resp?.data?.total ?? 0;
        setInvoices(list.map(mapApplication));
        setInvoiceTotal(total);
      } catch (err) {
        showToast(extractErrorMessage(err, "加载开票记录失败"), "error");
      } finally {
        setLoadingInvoices(false);
      }
    },
    [invoicePageNo, showToast],
  );

  // 当发票类型为「电子专用发票」时，「个人」抬头不可选；若当前选了「个人」则回退到「企业」
  useEffect(() => {
    if (isSpecialInvoice && titleType === "个人") {
      setTitleType("企业");
    }
  }, [isSpecialInvoice, titleType]);

  // 抬头类型为「个人」时，税号清空并禁用
  useEffect(() => {
    if (titleType === "个人") {
      setInvoiceTax("");
    } else if (!invoiceTax) {
      // 切回企业时，若税号为空，补回默认
      setInvoiceTax("91310115MA2K8X8P6T");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleType]);

  /**
   * 订单已是后端分页 + 后端过滤的结果：
   *   - keyword / invoiceStatus 走 query，后端按条件筛选并按页返回
   *   - 不再做客户端过滤 / 切片，orders 即当前页数据
   */
  const filteredOrders = orders;

  const orderPageCount = Math.max(
    1,
    Math.ceil(orderTotal / ORDER_PAGE_SIZE),
  );

  const filteredInvoices = useMemo(() => {
    if (!invoiceStatusFilter) return invoices;
    return invoices.filter((row) => row.status === invoiceStatusFilter);
  }, [invoices, invoiceStatusFilter]);

  /** 打开开票弹窗（新建 / 修改共用，详情可选用作回填） */
  const openInvoiceModal = useCallback(
    (order: OrderRow, detail?: InvoiceApplicationRespVO) => {
      setActiveOrder(order);
      if (detail) {
        const fallbackInvoiceType: InvoiceTypeKey = "电子普通发票";
        const resolvedInvoiceType: InvoiceTypeKey =
          (INVOICE_TYPE_LABEL_MAP[detail.invoiceType] as InvoiceTypeKey) ||
          (detail.invoiceTypeLabel as InvoiceTypeKey) ||
          fallbackInvoiceType;
        const fallbackTitleType: TitleTypeKey = "企业";
        const resolvedTitleType: TitleTypeKey =
          (TITLE_TYPE_LABEL_MAP[detail.titleType] as TitleTypeKey) ||
          (detail.titleTypeLabel as TitleTypeKey) ||
          fallbackTitleType;
        setInvoiceType(resolvedInvoiceType);
        setTitleType(resolvedTitleType);
        setInvoiceTitle(detail.titleName || "");
        setInvoiceTax(detail.taxNo || "");
        setInvoiceEmail(detail.email || "");
      } else {
        setInvoiceType("电子普通发票");
        setTitleType("企业");
        setInvoiceTitle("");
        setInvoiceTax("");
        setInvoiceEmail("");
        setEditingApplicationId(null);
      }
      setModalOpen(true);
    },
    [],
  );

  /** 「申请开票」按钮 / 「修改申请」入口 */
  const handleApplyInvoice = useCallback(
    (order: OrderRow) => {
      if (order.invoiceStatus === "开票中") {
        setActiveTab("invoices");
        showToast("当前状态：开票中");
        return;
      }
      openInvoiceModal(order);
    },
    [openInvoiceModal, showToast],
  );

  /** 发票记录表格里的「修改申请」按钮 */
  const handleEditInvoice = useCallback(
    async (invoice: InvoiceRow) => {
      const applicationId = invoice.applicationDbId;
      if (!applicationId) {
        showToast("未找到需要修改的申请", "error");
        return;
      }
      try {
        const resp = await getInvoiceApplicationDetailApi(applicationId);
        const detail = resp?.data;
        if (!detail) {
          showToast("未获取到申请详情", "error");
          return;
        }
        // 关联订单可能不在当前页（订单是服务端分页，本地只有 5 条）
        // 先查本地，找不到时按订单号再请求一次后端把它拉回来
        const orderRef = detail.orderNo;
        let target = orderRef
          ? orders.find((o) => o.orderNo === orderRef)
          : undefined;
        if (!target && orderRef) {
          try {
            const orderResp = await getInvoiceableOrdersApi(
              cleanParams({
                pageNo: 1,
                pageSize: 1000,
                keyword: orderRef,
              }),
            );
            const pagedList: InvoiceableOrderRespVO[] = Array.isArray(
              orderResp?.data,
            )
              ? (orderResp?.data as InvoiceableOrderRespVO[])
              : ((orderResp?.data as { list?: InvoiceableOrderRespVO[] })
                  ?.list || []);
            const hit = pagedList.find((o) => o.orderNo === orderRef);
            if (hit) target = mapOrder(hit);
          } catch {
            // 拉取失败时仍交给下面的"未找到"分支兜底
          }
        }
        if (!target) {
          showToast("对应订单未在当前列表中，无法修改", "error");
          return;
        }
        setEditingApplicationId(detail.id);
        setActiveTab("orders");
        openInvoiceModal(target, detail);
      } catch (err) {
        showToast(extractErrorMessage(err, "加载申请详情失败"), "error");
      }
    },
    [orders, openInvoiceModal, showToast],
  );

  /** 下载已开具的发票 PDF（走后端 download 接口） */
  const handleDownloadInvoice = useCallback(
    async (invoice: InvoiceRow) => {
      if (!invoice.applicationDbId) {
        showToast("未找到可下载的发票", "error");
        return;
      }
      const result = await downloadInvoiceApplicationApi(
        invoice.applicationDbId,
        invoice.invoiceNo
          ? `${invoice.invoiceNo}.pdf`
          : `invoice-${invoice.applicationDbId}.pdf`,
      );
      if (result.ok) {
        showToast("发票 PDF 已下载", "success");
      } else {
        showToast("下载失败，请稍后重试", "error");
      }
    },
    [showToast],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setActiveOrder(null);
  }, []);

  /** 提交开票申请（新建 / 修改） */
  const handleSubmitInvoice = useCallback(async () => {
    if (!activeOrder) {
      showToast("未找到需要开票的订单", "error");
      return;
    }
    const title = invoiceTitle.trim();
    const tax = invoiceTax.trim();
    const email = invoiceEmail.trim();
    if (!title) {
      showToast("请填写发票抬头", "error");
      return;
    }
    if (isSpecialInvoice && titleType !== "企业") {
      showToast("电子专用发票仅支持企业抬头", "error");
      return;
    }
    if (titleType === "企业" && !tax) {
      showToast("请填写统一社会信用代码", "error");
      return;
    }
    if (titleType === "企业" && !TAX_NO_REGEX.test(tax)) {
      showToast("税号格式不正确，应为 15 或 18 位由数字与大写字母组成的统一社会信用代码", "error");
      return;
    }
    if (!email || !EMAIL_REGEX.test(email)) {
      showToast("请填写正确的发票接收邮箱", "error");
      return;
    }

    const invoiceTypeCode =
      INVOICE_TYPE_CODE_MAP[invoiceType] ?? invoiceType;
    const titleTypeCode = TITLE_TYPE_CODE_MAP[titleType] ?? titleType;

    setSubmitting(true);
    try {
      // 订单类型：优先用列表行里后端返回的 orderType；后端暂未下发时回退 MEMBER_ORDER
      const orderType = activeOrder.orderType || "MEMBER_ORDER";
      if (editingApplicationId) {
        // 重新提交（修改申请）
        await resubmitInvoiceApplicationApi(editingApplicationId, {
          orderType,
          invoiceType: invoiceTypeCode,
          titleType: titleTypeCode,
          titleName: title,
          taxNo: titleType === "个人" ? undefined : tax,
          email,
        });
      } else {
        // 首次提交
        await createInvoiceApplicationApi({
          orderId: activeOrder.orderDbId,
          orderType,
          invoiceType: invoiceTypeCode,
          titleType: titleTypeCode,
          titleName: title,
          taxNo: titleType === "个人" ? undefined : tax,
          email,
        });
      }

      setModalOpen(false);
      setActiveOrder(null);
      setEditingApplicationId(null);
      setActiveTab("invoices");
      showToast("开票申请已提交，可在发票管理中查看进度", "success");

      // 提交后刷新相关数据
      void refreshSummary();
      void refreshOrders();
      void refreshInvoices();
    } catch (err) {
      showToast(extractErrorMessage(err, "提交失败，请稍后重试"), "error");
    } finally {
      setSubmitting(false);
    }
  }, [
    activeOrder,
    editingApplicationId,
    invoiceEmail,
    invoiceTax,
    invoiceTitle,
    invoiceType,
    isSpecialInvoice,
    refreshInvoices,
    refreshOrders,
    refreshSummary,
    showToast,
    titleType,
  ]);

  // ESC 关闭弹窗
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeModal]);

  // 首次加载：汇总 + 订单 + 申请记录
  useEffect(() => {
    void refreshSummary();
    void refreshOrders();
    void refreshInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 订单过滤条件变化时重新拉取（关键词 + 状态走服务端，回到第 1 页）
  useEffect(() => {
    setOrderPageNo(1);
    const timer = window.setTimeout(() => {
      void refreshOrders(orderKeyword, orderStatusFilter, 1);
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKeyword, orderStatusFilter]);

  // 订单页码变化时重新拉取（换页 = 重新请求）
  useEffect(() => {
    void refreshOrders(orderKeyword, orderStatusFilter, orderPageNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderPageNo]);

  // 发票记录状态变化时回到第 1 页并重新拉取
  useEffect(() => {
    setInvoicePageNo(1);
    void refreshInvoices(invoiceStatusFilter, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceStatusFilter]);

  // 发票记录页码变化时重新拉取
  useEffect(() => {
    void refreshInvoices(invoiceStatusFilter, invoicePageNo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePageNo]);

  const isRevisionMode = !!editingApplicationId;
  const showRejection =
    isRevisionMode && !!activeOrder?.rejectionReason;

  return (
    <div className="invoice-management-page">
      <main className="invoice-management-main">
        <div className="invoice-management-content">
          {/* Hero 区域 */}
          <section className="invoice-hero">
            <div className="invoice-hero-text">
              <div className="invoice-eyebrow">ACCOUNT & BILLING</div>
              <h2>管理订单、账单与发票</h2>
              <p>查看充值记录并提交开票申请。</p>
            </div>
            <div className="invoice-billing-summary">
              <div className="invoice-summary-card">
                <span>累计充值</span>
                <strong>{formatCurrency(totalRecharge)}</strong>
              </div>
              <div className="invoice-summary-card">
                <span>当前可开票</span>
                <strong>{formatCurrency(invoiceableAmount)}</strong>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <div className="invoice-tabs" role="tablist">
            <button
              type="button"
              className={`invoice-tab ${activeTab === "orders" ? "active" : ""}`}
              onClick={() => setActiveTab("orders")}
              data-tab="orders"
              role="tab"
              aria-selected={activeTab === "orders"}
            >
              充值订单
            </button>
            <button
              type="button"
              className={`invoice-tab ${activeTab === "invoices" ? "active" : ""}`}
              onClick={() => setActiveTab("invoices")}
              data-tab="invoices"
              role="tab"
              aria-selected={activeTab === "invoices"}
            >
              发票管理
            </button>
          </div>

          {/* 充值订单面板 */}
          <section
            className={`invoice-panel ${activeTab === "orders" ? "active" : ""}`}
            id="panel-orders"
            role="tabpanel"
          >
            <div className="invoice-panel-card">
              <div className="invoice-panel-head">
                <div>
                  <h3>充值订单</h3>
                  <p>仅已支付且未开票的订单可以申请开票。</p>
                </div>
                <div className="invoice-toolbar">
                  <input
                    className="invoice-search"
                    id="orderSearch"
                    placeholder="搜索订单号"
                    value={orderKeyword}
                    onChange={(e) => setOrderKeyword(e.target.value)}
                  />
                  <Select
                    className="invoice-select invoice-select--doc"
                    popupClassName="invoice-select-dropdown"
                    value={orderStatusFilter || undefined}
                    onChange={(v) => setOrderStatusFilter(v as string)}
                    options={INVOICE_STATUS_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label || "全部开票状态",
                    }))}
                    placeholder="全部开票状态"
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement || document.body
                    }
                  />
                </div>
              </div>
              <div
                className="invoice-table-wrap"
                style={{ minHeight: 360 }}
              >
                <table>
                  <thead>
                    <tr>
                      <th>订单号</th>
                      <th>订单内容</th>
                      <th>支付金额</th>
                      <th>支付时间</th>
                      <th>支付状态</th>
                      <th>开票状态</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="invoice-empty-tip">
                          没有匹配的订单
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map((row) => {
                        const tagCls = TAG_CLASS_MAP[row.invoiceStatus];
                        return (
                          <tr key={row.orderNo}>
                            <td className="invoice-mono">{row.orderNo}</td>
                            <td>
                              <strong>{row.content}</strong>
                            </td>
                            <td>{formatCurrency(row.amount)}</td>
                            <td>{row.payTime}</td>
                            <td>
                              <span className="invoice-tag green">已支付</span>
                            </td>
                            <td>
                              <span className={`invoice-tag ${tagCls}`}>
                                {row.invoiceStatus}
                              </span>
                            </td>
                            <td>
                              {row.invoiceStatus === "可开票" ? (
                                <button
                                  type="button"
                                  className="invoice-row-action"
                                  onClick={() => handleApplyInvoice(row)}
                                >
                                  申请开票
                                </button>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* 分页器（右下角，每页 5 条） */}
              {orderTotal > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 22px 16px",
                  }}
                >
                  <span
                    style={{ fontSize: 12, color: "#64748b" }}
                  >
                    显示 {(orderPageNo - 1) * ORDER_PAGE_SIZE + 1}–
                    {Math.min(orderPageNo * ORDER_PAGE_SIZE, orderTotal)} 条，
                    共 {orderTotal} 条
                  </span>
                  <MiniPaginator
                    page={orderPageNo}
                    pages={orderPageCount}
                    hasPrev={orderPageNo > 1}
                    hasNext={orderPageNo < orderPageCount}
                    onChange={(p) => setOrderPageNo(p)}
                    variant="workspace"
                    className="invoice-paginator"
                  />
                </div>
              )}
            </div>
          </section>

          {/* 发票管理面板 */}
          <section
            className={`invoice-panel ${activeTab === "invoices" ? "active" : ""}`}
            id="panel-invoices"
            role="tabpanel"
          >
            <div className="invoice-panel-card">
              <div className="invoice-panel-head">
                <div>
                  <h3>开票记录</h3>
                  <p>提交后可在这里查看开票进度与有效发票。</p>
                </div>
                <div className="invoice-toolbar">
                  <Select
                    className="invoice-select invoice-select--doc"
                    popupClassName="invoice-select-dropdown"
                    value={invoiceStatusFilter || undefined}
                    onChange={(v) => setInvoiceStatusFilter(v as string)}
                    options={INVOICE_RECORD_STATUS_OPTIONS.map((opt) => ({
                      value: opt.value,
                      label: opt.label || "全部状态",
                    }))}
                    placeholder="全部状态"
                    getPopupContainer={(triggerNode) =>
                      triggerNode.parentElement || document.body
                    }
                  />
                </div>
              </div>
              <div
                className="invoice-table-wrap"
                style={{ minHeight: 360 }}
              >
                <table>
                  <thead>
                    <tr>
                      <th>订单号</th>
                      <th>申请单号</th>
                      <th>发票抬头</th>
                      <th>发票类型</th>
                      <th>开票金额</th>
                      <th>申请时间</th>
                      <th>状态</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="invoice-empty-tip">
                          没有匹配的发票记录
                        </td>
                      </tr>
                    ) : (
                      filteredInvoices.map((row) => {
                        const tagCls = INVOICE_TAG_CLASS_MAP[row.status];
                        return (
                          <tr key={row.invoiceNo}>
                            <td className="invoice-mono">{row.orderNo}</td>
                            <td className="invoice-mono">{row.invoiceNo}</td>
                            <td>
                              <strong>{row.title}</strong>
                            </td>
                            <td>{row.type}</td>
                            <td>{formatCurrency(row.amount)}</td>
                            <td>{row.appliedAt}</td>
                            <td>
                              <span className={`invoice-tag ${tagCls}`}>
                                {row.status}
                              </span>
                            </td>
                            <td>
                              {row.downloadable ? (
                                <button
                                  type="button"
                                  className="invoice-row-action"
                                  onClick={() => handleDownloadInvoice(row)}
                                >
                                  下载发票
                                </button>
                              ) : row.status === "待修改" ? (
                                <button
                                  type="button"
                                  className="invoice-row-action"
                                  onClick={() => handleEditInvoice(row)}
                                >
                                  修改申请
                                </button>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* 分页器（右下角，每页 5 条） */}
              {invoiceTotal > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "12px 22px 16px",
                  }}
                >
                  <span
                    style={{ fontSize: 12, color: "#64748b" }}
                  >
                    显示 {(invoicePageNo - 1) * INVOICE_PAGE_SIZE + 1}–
                    {Math.min(invoicePageNo * INVOICE_PAGE_SIZE, invoiceTotal)} 条，
                    共 {invoiceTotal} 条
                  </span>
                  <MiniPaginator
                    page={invoicePageNo}
                    pages={Math.max(
                      1,
                      Math.ceil(invoiceTotal / INVOICE_PAGE_SIZE),
                    )}
                    hasPrev={invoicePageNo > 1}
                    hasNext={
                      invoicePageNo <
                      Math.max(
                        1,
                        Math.ceil(invoiceTotal / INVOICE_PAGE_SIZE),
                      )
                    }
                    onChange={(p) => setInvoicePageNo(p)}
                    variant="workspace"
                    className="invoice-paginator"
                  />
                </div>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Modal */}
      <div
        className={`invoice-modal-overlay ${modalOpen ? "show" : ""}`}
        aria-hidden={!modalOpen}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <section
          className="invoice-modal large"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invoiceModalTitle"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="invoice-modal-head">
            <h3 id="invoiceModalTitle">
              {isRevisionMode ? "修改开票申请" : "申请开票"}
            </h3>
            <button
              type="button"
              className="invoice-close"
              aria-label="关闭"
              onClick={closeModal}
            >
              <X size={16} aria-hidden />
            </button>
          </div>
          <div className="invoice-modal-body">
            <div
              className={`invoice-rejection-alert ${showRejection ? "show" : ""}`}
            >
              <AlertCircle size={16} aria-hidden />
              <div>
                <strong>财务退回原因</strong>
                <span>
                  {(() => {
                    const label =
                      activeOrder?.rejectionReasonLabel ||
                      (activeOrder?.rejectionReason
                        ? REJECTION_REASON_LABEL[activeOrder.rejectionReason] ||
                          activeOrder.rejectionReason
                        : "");
                    if (!label) return "";
                    const note = activeOrder?.rejectionNote?.trim();
                    return note ? `${label}：${note}` : `${label}：`;
                  })()}
                </span>
              </div>
            </div>

            <div className="invoice-amount-box">
              <span>本次开票金额</span>
              <strong>
                {activeOrder ? formatCurrency(activeOrder.amount) : "¥0.00"}
              </strong>
            </div>

            <div className="invoice-form-grid">
              <div className="invoice-form-group">
                <label htmlFor="invoiceType">
                  发票类型<em>*</em>
                </label>
                <Select
                  className="invoice-input-select invoice-input-select--doc"
                  popupClassName="invoice-select-dropdown"
                  id="invoiceType"
                  value={invoiceType}
                  onChange={(v) => setInvoiceType(v as InvoiceTypeKey)}
                  options={[
                    { value: "电子普通发票", label: "电子普通发票" },
                    { value: "电子专用发票", label: "电子专用发票" },
                  ]}
                  getPopupContainer={(triggerNode) =>
                    triggerNode.parentElement || document.body
                  }
                />
              </div>
              <div className="invoice-form-group">
                <label htmlFor="invoiceTitleType">
                  抬头类型<em>*</em>
                </label>
                <Select
                  className="invoice-input-select invoice-input-select--doc"
                  popupClassName="invoice-select-dropdown"
                  id="invoiceTitleType"
                  value={titleType}
                  onChange={(v) => setTitleType(v as TitleTypeKey)}
                  options={[
                    { value: "企业", label: "企业", disabled: isSpecialInvoice },
                    { value: "个人", label: "个人", disabled: isSpecialInvoice },
                  ]}
                  getPopupContainer={(triggerNode) =>
                    triggerNode.parentElement || document.body
                  }
                />
              </div>
              <div className="invoice-form-group full">
                <label htmlFor="invoiceTitle">
                  发票抬头<em>*</em>
                </label>
                <input
                  className="invoice-input"
                  id="invoiceTitle"
                  placeholder="请输入发票抬头"
                  autoComplete="organization"
                  value={invoiceTitle}
                  onChange={(e) => setInvoiceTitle(e.target.value)}
                />
              </div>
              <div className="invoice-form-group full">
                <label htmlFor="invoiceTax">
                  税号
                  {titleType === "企业" ? <em>*</em> : null}
                </label>
                <input
                  className="invoice-input"
                  id="invoiceTax"
                  placeholder={
                    titleType === "企业"
                      ? "请输入统一社会信用代码"
                      : "个人抬头无需填写税号"
                  }
                  disabled={titleType === "个人"}
                  value={invoiceTax}
                  onChange={(e) => setInvoiceTax(e.target.value)}
                />
              </div>
              <div className="invoice-form-group full">
                <label htmlFor="invoiceEmail">
                  发票接收邮箱<em>*</em>
                </label>
                <input
                  className="invoice-input"
                  id="invoiceEmail"
                  type="email"
                  placeholder="name@example.com"
                  value={invoiceEmail}
                  onChange={(e) => setInvoiceEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="invoice-form-note">
              <Info size={15} aria-hidden />
              <span>
                电子普通发票支持企业或个人抬头；电子专用发票仅支持企业抬头。
              </span>
            </div>
          </div>
          <div className="invoice-modal-foot">
            <span className="invoice-submit-hint">
              发票预计在 1～3 个工作日内开具，并发送至您的邮箱。请确认抬头和税号准确；发票开具后如需修改，请联系工作人员。
            </span>
            <button
              type="button"
              className="invoice-button"
              onClick={closeModal}
            >
              取消
            </button>
            <button
              type="button"
              className="invoice-button primary"
              onClick={handleSubmitInvoice}
            >
              {isRevisionMode ? "重新提交" : "确认提交"}
            </button>
          </div>
        </section>
      </div>

      {/* Toast（项目统一的顶部 AppToast） */}
      <AppToast
        config={toastConfig}
        onClose={() => setToastConfig(null)}
      />
    </div>
  );
};

export default InvoiceManagement;
